/**
 * Main lobby (the home screen after sign-in), top to bottom: a rematch you were invited to; the
 * logo and your name; Quick Play, big; tiles for New Game, the Tutorial and your stats; your Play
 * Style; open games waiting for players; then, folded away with their counts, games in progress
 * anyone can watch (with how long each has been idle) and your recent games with their recap and
 * replay.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { t, type GameLogSummary, type LobbySummary } from '@kadice/shared';
import { api } from '../api';
import type { PlayStyle } from '../options';
import type { GameClient } from '../useGameClient';
import { Avatar } from './Avatar';
import type { ReviewMode } from './Review';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Now, updated every half minute (the idle times count up between lobby list updates). */
function useNow(): number {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), MINUTE / 2);
    return () => clearInterval(id);
  }, []);
  return now;
}

/** 'Active now' for a couple of minutes (the list's idle time may be up to a minute old), then the idle time. */
function idleText(ms: number): string {
  if (ms < 2 * MINUTE) return t('lobby.activeNow');
  if (ms < HOUR) return t('lobby.idleMinutes', { n: Math.floor(ms / MINUTE) });
  if (ms < DAY) return t('lobby.idleHours', { n: Math.floor(ms / HOUR) });
  return t('lobby.idleDays', { n: Math.floor(ms / DAY) });
}

export function LobbyList({
  client,
  onReview,
  playStyle,
  onPlayStyle,
  onTutorial,
  onStats,
}: {
  client: GameClient;
  onReview: (gameId: number, mode: ReviewMode) => void;
  /** Your Play Style: a game you open starts with the matching Card Selection. */
  playStyle: PlayStyle;
  onPlayStyle: (style: PlayStyle) => void;
  onTutorial: () => void;
  onStats: () => void;
}) {
  const { lobbies, request, invite, dismissInvite, me } = client;
  const open = lobbies.filter((l) => !l.inProgress);
  const playing = lobbies.filter((l) => l.inProgress);
  const now = useNow();
  // A rematch invite shows while its lobby is still open.
  const invited = invite && open.find((l) => l.gameId === invite.gameId);
  return (
    <section className="home">
      {invite && invited && (
        <div className="invite-banner" role="status">
          <span>{t('lobby.rematchInvite', { name: invite.fromName })}</span>
          <span className="spacer" />
          <button
            className="primary"
            onClick={() => {
              dismissInvite();
              void request('lobby:join', { gameId: invite.gameId });
            }}
          >
            {t('lobby.join')}
          </button>
          <button onClick={dismissInvite} aria-label={t('common.close')} title={t('common.close')}>
            ×
          </button>
        </div>
      )}

      <header className="home-hero">
        <img src="/img/logo.png" alt={t('app.title')} className="home-logo" />
        {me && (
          <p className="home-hello">
            <Avatar playerId={me.id} />
            <span>{t('lobby.hello', { name: me.displayName })}</span>
          </p>
        )}
      </header>

      <button
        className="home-quick"
        onClick={() => void request('lobby:quickPlay', { cardSelection: playStyle })}
        title={t('lobby.quickPlayHint')}
      >
        <span className="home-quick-icon" aria-hidden="true">
          🎲
        </span>
        <span className="home-quick-text">
          <strong>{t('lobby.quickPlay')}</strong>
          <small>{t('lobby.quickPlaySub')}</small>
        </span>
        <span className="home-quick-go" aria-hidden="true">
          ▶
        </span>
      </button>

      <div className="home-tiles">
        <Tile
          icon="➕"
          label={t('lobby.newGame')}
          sub={t('lobby.newGameSub')}
          onClick={() => void request('lobby:create', { cardSelection: playStyle })}
        />
        <Tile icon="🎓" label={t('lobby.tutorial')} sub={t('lobby.tutorialSub')} onClick={onTutorial} />
        <Tile icon="📊" label={t('lobby.stats')} sub={t('lobby.statsSub')} onClick={onStats} />
      </div>

      <div className="home-style">
        <div className="home-style-label">
          <strong>{t('options.playStyle')}</strong>
          <small className="muted">{t('options.playStyleHint')}</small>
        </div>
        <div className="segmented" role="radiogroup" aria-label={t('options.playStyle')}>
          {(['choose', 'random'] as const).map((style) => (
            <button
              key={style}
              role="radio"
              aria-checked={playStyle === style}
              className={playStyle === style ? 'on' : undefined}
              onClick={() => onPlayStyle(style)}
            >
              <span>{t(`options.${style}`)}</span>
              <small>{t(`options.${style}Hint`)}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="home-section">
        <h2>
          {t('lobby.openGames')} {open.length > 0 && <span className="home-count">{open.length}</span>}
        </h2>
        {open.length === 0 ? (
          <p className="home-empty">{t('lobby.noGames')}</p>
        ) : (
          <ul className="lobbies">
            {open.map((l) => (
              <li key={l.gameId}>
                <GameInfo lobby={l} />
                <span className="muted">{t('lobby.seats', { n: l.players.length, max: l.maxPlayers })}</span>
                <button
                  className="primary"
                  disabled={l.players.length >= l.maxPlayers}
                  onClick={() => void request('lobby:join', { gameId: l.gameId })}
                >
                  {t('lobby.join')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {playing.length > 0 && (
        <Fold title={t('lobby.inProgress')} count={playing.length}>
          <ul className="lobbies">
            {playing.map((l) => {
              const idle = Math.max(0, now - Date.parse(l.idleSince));
              return (
                <li key={l.gameId} className={idle >= DAY ? 'stale' : undefined}>
                  <GameInfo lobby={l} />
                  <span className="muted">{t('lobby.left', { n: l.players.length })}</span>
                  <button onClick={() => void request('lobby:watch', { gameId: l.gameId })}>{t('lobby.watch')}</button>
                  <span className="muted small lobby-idle">{idleText(idle)}</span>
                </li>
              );
            })}
          </ul>
        </Fold>
      )}

      <RecentGames onReview={onReview} />
    </section>
  );
}

/** A square action under Quick Play: icon, name, a few words on what it does. */
function Tile({ icon, label, sub, onClick }: { icon: string; label: string; sub: string; onClick: () => void }) {
  return (
    <button className="home-tile" onClick={onClick}>
      <span className="home-tile-icon" aria-hidden="true">
        {icon}
      </span>
      <strong>{label}</strong>
      <small>{sub}</small>
    </button>
  );
}

/** A list folded under its heading and count; tap the heading to open it. */
function Fold({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    <details className="home-section home-fold">
      <summary>
        <h2>
          {title} <span className="home-count">{count}</span>
        </h2>
      </summary>
      {children}
    </details>
  );
}

/** Your last counted games, each with its recap and replay. Loaded each time you're back in the main lobby. */
function RecentGames({ onReview }: { onReview: (gameId: number, mode: ReviewMode) => void }) {
  const [games, setGames] = useState<GameLogSummary[] | null>(null);
  useEffect(() => {
    let live = true;
    api.recentGames().then(
      (g) => live && setGames(g),
      () => live && setGames([]),
    );
    return () => {
      live = false;
    };
  }, []);
  if (!games || games.length === 0) return null;
  return (
    <Fold title={t('lobby.recentGames')} count={games.length}>
      <ul className="lobbies recent">
        {games.map((g) => (
          <li key={g.gameId}>
            <div>
              <strong>
                {g.winnerName ? t('lobby.recentWinner', { name: g.winnerName }) : t('lobby.recentNoWinner')}
              </strong>
              <div className="muted">
                {t('lobby.recentLine', {
                  names: g.players.map((p) => p.name).join(t('list.separator')),
                  date: new Date(g.endedAt ?? g.startedAt).toLocaleDateString(),
                })}
              </div>
            </div>
            <button onClick={() => onReview(g.gameId, 'recap')}>{t('end.recap')}</button>
            <button onClick={() => onReview(g.gameId, 'replay')}>{t('lobby.replay')}</button>
          </li>
        ))}
      </ul>
    </Fold>
  );
}

function GameInfo({ lobby: l }: { lobby: LobbySummary }) {
  return (
    <div>
      <strong>{t('lobby.gameOf', { name: l.ownerName })}</strong>
      <div className="muted">
        {l.players.join(t('list.separator'))}
        {l.spectators > 0 && t('lobby.watchers', { n: l.spectators })}
      </div>
    </div>
  );
}
